import type { FC } from 'react'
import { Link } from 'react-router'
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import { EmptyState, Loader } from '~/shared/ui'
import type { PatientUser } from '~/shared/types'
import { useSiblingTabPath } from '~/shared/hooks/useRouteBasePath'
import { useGetPersonCareGaps, type PersonCareGap } from './PersonCareGapsService'
// The status strings are server-owned (MeasureReport.care_gap_status); this maps them to
// chip colors for display only — nothing here re-derives whether a gap is open.
const STATUS_COLOR: Record<string, 'error' | 'warning' | 'success' | 'default'> = {
  Overdue: 'error',
  'Due Soon': 'warning',
  Satisfied: 'success',
  Excluded: 'default',
}

const formatDate = (iso: string): string =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })

const CareGapRow: FC<{ gap: PersonCareGap; todosPath: string }> = ({ gap, todosPath }) => (
  <TableRow data-testid={`care-gap-${gap.id}`} sx={{ '& td': { borderColor: 'grey.100' } }}>
    <TableCell>
      <Typography variant="body2" sx={{ fontWeight: gap.is_care_gap ? 600 : 400 }}>
        {gap.name ?? gap.measure_config ?? '—'}
      </Typography>
      {gap.detail && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {gap.detail}
        </Typography>
      )}
    </TableCell>
    <TableCell>
      {gap.care_gap_status && (
        <Chip
          size="small"
          label={gap.care_gap_status}
          color={STATUS_COLOR[gap.care_gap_status] ?? 'default'}
          data-testid={`care-gap-status-${gap.id}`}
        />
      )}
    </TableCell>
    <TableCell>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {gap.next_action_date ? formatDate(gap.next_action_date) : '—'}
      </Typography>
    </TableCell>
    <TableCell align="right">
      {gap.case_id != null && (
        <Link
          to={`${todosPath}?caseId=${gap.case_id}`}
          data-testid={`care-gap-case-link-${gap.id}`}
          style={{ textDecoration: 'none' }}
        >
          <Box sx={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
            <Typography variant="body2" sx={{ color: 'primary.main' }}>
              Open case
            </Typography>
            <ChevronRightIcon fontSize="small" color="primary" />
          </Box>
        </Link>
      )}
    </TableCell>
  </TableRow>
)

/**
 * The member's quality-measure care gaps, with the case to work each open gap in.
 *
 * Rows come from the CRM BFF already scoped to active measures, status-derived, and
 * ordered open-gaps-first. "Open case →" deep-links to the member's To Dos tab with the
 * gap's case selected, which is where the care-gap case is worked.
 */
const CareGaps: FC<{ patient: PatientUser }> = ({ patient }) => {
  // Sibling tab under the same profile, in either nav mode (/patients/:id or /people/:id).
  const todosPath = useSiblingTabPath('todos')
  const {
    data,
    isPending: isLoading,
    error,
  } = useGetPersonCareGaps({ personId: patient.person?.id })

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Loader inline />
      </Box>
    )
  }

  if (error) {
    return (
      <EmptyState
        icon={<InfoOutlinedIcon />}
        title="Couldn't load care gaps"
        caption="Something went wrong fetching this member's care gaps. Please try again."
      />
    )
  }

  const careGaps = data?.care_gaps ?? []

  if (careGaps.length === 0) {
    return (
      <EmptyState
        icon={<InfoOutlinedIcon />}
        title="No care gaps"
        caption="This member has no quality measures being tracked, so there are no care gaps to show."
      />
    )
  }

  const openGapCount = careGaps.filter(gap => gap.is_care_gap).length

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
        {openGapCount} open of {careGaps.length} measure{careGaps.length === 1 ? '' : 's'}
      </Typography>
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { bgcolor: 'grey.50', borderColor: 'grey.100' } }}>
              <TableCell>Measure</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Next action</TableCell>
              <TableCell align="right">Case</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {careGaps.map(gap => (
              <CareGapRow key={gap.id} gap={gap} todosPath={todosPath} />
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  )
}

export default CareGaps
